<template>
  <div>
    <app-spinner v-if="!cash_data" />
    <template v-else>
      <app-cells>
        <h1 class="title">Group expenses (ID: {{ $route.params.cashId }})</h1>
        <div class="status-title" :class="{ 'status-title--checked': cash_data.checked, 'status-title--not-checked': !cash_data.checked }">
          {{ cash_data.checked ? 'Checked' : 'Not checked' }}
        </div>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Given TSh:</span> <span>{{ cash_data.given_tsh ? cash_data.given_tsh.toLocaleString() : 0 }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Given USD:</span> <span>{{ cash_data.given_usd ? cash_data.given_usd.toLocaleString() : 0 }}</span>
          </div>
          <div class="info-cards__row-item info-cards__row-item--full">
            <span>Given by:</span>
            <router-link
              v-if="cash_data.given_by"
              :to="{ name: 'employees-profile', params: { id: cash_data.given_by.id } }"
              class="info-cards__link"
            >
              {{ cash_data.given_by.full_name }}
            </router-link>
            <span v-else>-</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Difference TSh:</span> <span>{{ cash_data.group_expenses_difference_tsh ? cash_data.group_expenses_difference_tsh.toLocaleString() : 0 }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Difference USD:</span> <span>{{ cash_data.group_expenses_difference_usd ? cash_data.group_expenses_difference_usd.toLocaleString() : 0 }}</span>
          </div>
          <div class="info-cards__row-item info-cards__row-item--full">
            <span>Responsible:</span>
            <router-link
              v-if="cash_data.responsible"
              :to="{ name: 'employees-profile', params: { id: cash_data.responsible.id } }"
              class="info-cards__link"
            >
              {{ cash_data.responsible.full_name }}
            </router-link>
            <span v-else>-</span>
          </div>
        </div>
      </div>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #date="{ row, index }">
          <app-date-picker
            v-if="index !== table.items.length && !cash_data.checked"
            @change="onUpdateTableData(row.id, { date: row.date })"
            v-model="row.date"
            placeholder="Specify a date"
            :clearable="false"
          />
          <span v-if="cash_data.checked">{{ dateFormat(row.date) }}</span>
          <app-date-picker
            v-if="index === table.items.length && !cash_data.checked"
            @change="onSelectDate"
            v-model="new_row.date"
            placeholder="Specify a date"
            :clearable="false"
            ref="new_expense_date"
          />
        </template>
        <template #type="{ row, index }">
          <app-select
            v-if="index !== table.items.length && !cash_data.checked"
            @input="onUpdateTableData(row.id, { type: row.type })"
            v-model="row.type"
            :options="expense_type_options"
            :reduce="item => item.code"
            :clearable="false"
            :calculate-position="withPopper"
            :append-to-body="true"
            placeholder="Select type"
            class="select--250"
            :tabindex="2"
          />
          <span v-if="cash_data.checked">{{ findExpenseType(row.type) }}</span>
          <app-select
            v-if="index === table.items.length && !cash_data.checked"
            @input="onSelectType"
            v-model="new_row.type"
            :reduce="item => item.code"
            :options="expense_type_options"
            :clearable="false"
            :calculate-position="withPopper"
            :append-to-body="true"
            placeholder="Select type"
            class="select--250"
            :tabindex="2"
            ref="new_expense_type"
          />
        </template>
        <template #amount_tsh="{ row, index }">
          <input
            v-if="index !== table.items.length && !cash_data.checked"
            v-model.number="row.amount_tsh"
            @change="onUpdateTableData(row.id, { amount_tsh: row.amount_tsh })"
            class="input input--250"
            type="number"
            min="0"
            style="text-align: center"
            :tabindex="2"
          />
          <span v-if="cash_data.checked">{{ row.amount_tsh ? row.amount_tsh.toLocaleString() : '-' }}</span>
          <input
            v-if="index === table.items.length && !cash_data.checked"
            v-model.number="new_row.amount_tsh"
            @keydown.enter="onNewRowSubmit"
            class="input input--250"
            type="number"
            min="0"
            style="text-align: center"
            :tabindex="2"
            ref="new_expense_amount"
          />
        </template>
        <template #amount_usd="{ row, index }">
          <input
            v-if="index !== table.items.length && !cash_data.checked"
            v-model.number="row.amount_usd"
            @change="onUpdateTableData(row.id, { amount_usd: row.amount_usd })"
            class="input input--250"
            type="number"
            min="0"
            style="text-align: center"
            :tabindex="2"
          />
          <span v-if="cash_data.checked">{{ row.amount_usd ? row.amount_usd.toLocaleString() : '-' }}</span>
          <input
            v-if="index === table.items.length && !cash_data.checked"
            v-model.number="new_row.amount_usd"
            @keydown.enter="onNewRowSubmit"
            class="input input--250"
            type="number"
            min="0"
            style="text-align: center"
            ref="new_qty"
            :tabindex="2"
          />
        </template>
        <template #controls="{ row, index }">
          <div v-if="index !== table.items.length" class="table-default__controls">
            <button @click="onShowControlModal(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells
        v-if="!cash_data.checked && (cash_data.group_expenses_difference_tsh || cash_data.group_expenses_difference_usd)"
        position="end"
        class="cells--end-vertical"
      >
        <app-select
          @search="onEmployeesSearch"
          v-model="responsible"
          :options="employee_options"
          :filterable="false"
          :clearable="false"
          :display-field="option => `${option.first_name} ${option.last_name}`"
          label="first_name"
          placeholder="Responsible"
          class="select--200"
          :error="$v.responsible.$error"
        />
        <app-button @click.native="onShowControlModal(null)">Mark as checked</app-button>
      </app-cells>
    </template>
    <modal
      name="control-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideControlModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          {{ expense_to_delete ? 'Delete expense' : 'Mark as checked' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideControlModal" theme="dark">Cancel</app-button>
            <app-button v-if="expense_to_delete" @click.native="onDeleteExpense" ref="delete">Yes</app-button>
            <app-button v-else @click.native="onMarkChecked" ref="mark">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteSafariGroupsCashExpense,
  getEmployeesList,
  getSafariExpenseType,
  getSafariGroupsCash,
  getSafariGroupsCashExpensesList,
  patchSafariGroupsCash,
  patchSafariGroupsCashExpense,
  postSafariGroupsCashExpense,
} from '@/http'
import { createPopper } from '@popperjs/core'
import { format, parse } from 'date-fns'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'SafariGroupsDetailExpensesDetail',
  data() {
    return {
      cash_data: null,
      table: {
        items: [],
        columns: ['date', 'type', 'amount_tsh', 'amount_usd'],
        options: {
          headings: {
            'date': 'Date',
            'type': 'Type',
            'amount_tsh': 'Amount TSh',
            'amount_usd': 'Amount USD',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      new_row: {},
      expense_type_options: [],
      placement: 'bottom',
      expense_to_delete: null,
      responsible: null,
      employee_options: [],
    }
  },
  validations: {
    responsible: { required },
  },
  created() {
    document.title = `Safari group expenses (ID: ${ this.$route.params.cashId }) | Altezza Board`
    getSafariGroupsCash(this.$route.params.id, this.$route.params.cashId)
      .then(response => {
        this.cash_data = response.data
        if (!this.cash_data.checked) this.table.columns.push('controls')
        this.fetchItems()
      })
    getSafariExpenseType().then(response => { this.expense_type_options = response.data })
  },
  methods: {
    fetchData() {
      getSafariGroupsCash(this.$route.params.id, this.$route.params.cashId)
        .then(response => {
          this.cash_data = response.data
        })
    },
    fetchItems() {
      getSafariGroupsCashExpensesList(this.$route.params.cashId)
        .then(response => {
          this.table.items = response.data
          if (!this.cash_data.checked) this.table.items.push({})
        })
    },
    onMarkChecked() {
      patchSafariGroupsCash(this.$route.params.id, this.$route.params.cashId, { checked: true, responsible: this.responsible.id })
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Cash checked',
          })
          this.onHideControlModal()
        })
    },
    onShowControlModal(id) {
      if (!id) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.$notify({
            type: 'error',
            title: 'Check that the fields are filled in correctly',
          })
          return
        }
      } else this.expense_to_delete = id
      this.$modal.show('control-modal')
    },
    onHideControlModal() {
      this.expense_to_delete = null
      this.$modal.hide('control-modal')
    },
    onDeleteExpense() {
      deleteSafariGroupsCashExpense(this.$route.params.cashId, this.expense_to_delete)
        .then(res => {
          this.table.items = res.data
          this.table.items.push({})
          this.$notify({
            type: 'success',
            title: 'Expense deleted',
          })
          this.onHideControlModal()
        })
    },
    onUpdateTableData(rowId, data) {
      patchSafariGroupsCashExpense(this.$route.params.cashId, rowId, data)
        .then(res => {
          this.table.items = res.data
          this.table.items.push({})
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Expense updated',
          })
        })
    },
    onNewRowSubmit() {
      if (this.new_row.date && this.new_row.type && (this.new_row.amount_tsh || this.new_row.amount_tsh === 0) && (this.new_row.amount_usd || this.new_row.amount_usd === 0)) {

        postSafariGroupsCashExpense(this.$route.params.cashId, this.new_row)
          .then(res => {
            this.fetchData()
            this.table.items = res.data
            this.table.items.push({})
            this.new_row = {}
            this.$nextTick(() => {
              this.$refs.new_expense_date.$children[0].focus()
            })
            this.$notify({
              type: 'success',
              title: 'Expense added',
            })
          })
      } else {
        this.$notify({
          type: 'error',
          title: 'Check that the table row fields are filled in correctly',
        })
      }
    },
    onSelectDate() {
      this.$nextTick(() => {
        this.$refs.new_expense_type.$el.querySelector('input').focus()
      })
    },
    onSelectType() {
      this.$nextTick(() => {
        this.$refs.new_expense_amount.focus()
      })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    withPopper(dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    },
    onEmployeesSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.employee_options = []
      } else {
        getEmployeesList({search}).then(response => {
          vm.employee_options = response.data
          loading(false)
        })
      }
    }, 350),
    findExpenseType(type_code) {
      return this.expense_type_options.find(item => item.code === type_code).name
    }
  }
}
</script>
